import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import labelKey from '../../localization/label.json';
import {
  useSurveyActionMutation,
  useSurveyCloseMutation,
  useSurveyLaterMutation,
  useSurveySubmitMutation,
} from '../../../services/SurveyApi';
import {isLowSurveyRating, ratingChoicesForQuestion} from './surveyRatingOptions';
import {
  replaceSurveyTokens,
  SURVEY_DEFAULT_QUESTIONS,
  SURVEY_POPUP_DEFAULTS,
  SURVEY_PREVIEW_DEFAULT_REASONS,
} from './surveyPopupDefaults';
import { useAppSelector } from '../../../hooks';
import { selectAuth } from '../../../features/authSlice';

export type SurveyReasonOption = {
  surveyReasonId: number;
  reason: string;
  surveyQuestionId?: number;
};

export type SurveyQuestionOption = {
  surveyQuestionId: number;
  question: string;
  showSmileys?: boolean;
  smileys?: { emoji: string; text: string }[];
  reasons?: SurveyReasonOption[];
};

export type SurveyData = {
  surveyId: number;
  campaignNumber: string;
  title?: string;
  description?: string;
  welcomeHeading?: string;
  welcomeMessage?: string;
  actionHeading?: string;
  actionMessage?: string;
  popupInstanceId: string;
  questions?: SurveyQuestionOption[] | { question: string }[] | string[];
  reasons?: SurveyReasonOption[];
};

type Props = {
  show: boolean;
  survey: SurveyData | null;
  onClose: () => void;
  /** When true, UI navigates without calling survey APIs (admin preview). */
  previewMode?: boolean;
  /** Render popup body without Bootstrap Modal (for stepper preview). */
  embedded?: boolean;
  /**
   * Admin preview: jump to a survey screen without completing prior steps.
   * Feedback seeds a low rating so Action/reasons content is visible.
   */
  previewForceStep?: 'rating' | 'feedback' | 'thanks';
};

const normalizeReasonList = (
  nested: any[],
  flatReasons: SurveyReasonOption[],
  questionId: number
): SurveyReasonOption[] => {
  const fromNested = (nested || [])
    .map((r: any, ri: number) => {
      if (typeof r === 'string') {
        const reason = r.trim();
        if (!reason) return null;
        return {
          surveyReasonId: questionId * 100 + ri + 1,
          reason,
          surveyQuestionId: questionId,
        };
      }
      const reason = String(r?.reason ?? '').trim();
      if (!reason) return null;
      return {
        surveyReasonId: Number(r?.surveyReasonId) || questionId * 100 + ri + 1,
        reason,
        surveyQuestionId: Number(r?.surveyQuestionId) || questionId,
      };
    })
    .filter(Boolean) as SurveyReasonOption[];

  if (fromNested.length > 0) return fromNested;

  const fromFlat = (flatReasons || []).filter(
    (r) => !r.surveyQuestionId || Number(r.surveyQuestionId) === questionId
  );
  if (fromFlat.length > 0) return fromFlat;

  return SURVEY_PREVIEW_DEFAULT_REASONS.map((r) => ({
    ...r,
    surveyQuestionId: questionId,
  }));
};

const normalizeQuestions = (survey: SurveyData | null): SurveyQuestionOption[] => {
  const raw = survey?.questions || [];
  if (!raw.length) {
    return [
      {
        surveyQuestionId: 1,
        question: SURVEY_DEFAULT_QUESTIONS[0],
        reasons: SURVEY_PREVIEW_DEFAULT_REASONS.map((r) => ({
          ...r,
          surveyQuestionId: 1,
        })),
      },
    ];
  }

  const flatReasons = survey?.reasons || [];
  const usedIds = new Set<number>();

  const normalized = raw
    .map((q: any, index: number) => {
      if (typeof q === 'string') {
        let id = index + 1;
        while (usedIds.has(id)) id += 1000;
        usedIds.add(id);
        return {
          surveyQuestionId: id,
          question: q.trim(),
          showSmileys: true,
          smileys: undefined,
          reasons: normalizeReasonList([], flatReasons, id),
        };
      }

      let id = Number(q?.surveyQuestionId);
      if (!Number.isFinite(id) || id <= 0 || usedIds.has(id)) {
        id = index + 1;
        while (usedIds.has(id)) id += 1000;
      }
      usedIds.add(id);

      const nested = Array.isArray(q?.reasons) ? q.reasons : [];
      return {
        surveyQuestionId: id,
        question: String(q?.question ?? q?.text ?? '').trim(),
        showSmileys: q?.showSmileys !== false,
        smileys: Array.isArray(q?.smileys) ? q.smileys : undefined,
        reasons: normalizeReasonList(nested, flatReasons, id),
      };
    })
    .filter((q) => q.question.length > 0);

  if (!normalized.length) {
    return [
      {
        surveyQuestionId: 1,
        question: SURVEY_DEFAULT_QUESTIONS[0],
        reasons: SURVEY_PREVIEW_DEFAULT_REASONS.map((r) => ({
          ...r,
          surveyQuestionId: 1,
        })),
      },
    ];
  }

  return normalized;
};

const SurveyModal: React.FC<Props> = ({
  show,
  survey,
  onClose,
  previewMode = false,
  embedded = false,
  previewForceStep,
}) => {
  const {
    email: authEmail,
    firstName: authFirstName,
    lastName: authLastName,
    role: authRole,
    designationName,
    departmentName,
  } = useAppSelector(selectAuth);
  const [step, setStep] = useState<'rating' | 'feedback' | 'thanks'>(
    previewForceStep || 'rating'
  );
  const [ratings, setRatings] = useState<Record<number, number>>({});
  const [hoverByQuestion, setHoverByQuestion] = useState<Record<number, number>>({});
  const [reasonByQuestion, setReasonByQuestion] = useState<Record<number, number | null>>({});
  const [feedback, setFeedback] = useState('');
  const [allowContact, setAllowContact] = useState(false);
  const [email, setEmail] = useState('');
  const [validationError, setValidationError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const openTimeRef = useRef<Date>(new Date());
  const sessionIdRef = useRef<string>(`web-${Date.now()}`);

  const accountEmail = (authEmail || '').trim();
  const tokenValues = {
    FirstName: authFirstName,
    LastName: authLastName,
    Email: authEmail,
    RoleName: authRole,
    DesignationName: designationName,
    DepartmentName: departmentName,
  };
  const welcomeHeading = replaceSurveyTokens(
    survey?.welcomeHeading || SURVEY_POPUP_DEFAULTS.welcomeHeading,
    tokenValues
  );
  const welcomeMessage = replaceSurveyTokens(
    survey?.welcomeMessage || SURVEY_POPUP_DEFAULTS.welcomeMessage,
    tokenValues
  );
  const actionHeading = replaceSurveyTokens(
    survey?.actionHeading || SURVEY_POPUP_DEFAULTS.actionHeading,
    tokenValues
  );
  const actionMessage = replaceSurveyTokens(
    survey?.actionMessage || SURVEY_POPUP_DEFAULTS.actionMessage,
    tokenValues
  );
  const thanksTitle = replaceSurveyTokens(
    labelKey.surveyThankYouTitle || 'Thank you {FirstName}!',
    tokenValues
  );
  const thanksMessage = replaceSurveyTokens(
    labelKey.surveyThankYouMessage ||
      'Thank you {FirstName} for using SpendSail to track your corporate expenses. We appreciate your feedback.',
    tokenValues
  );

  const questions = useMemo(() => normalizeQuestions(survey), [survey]);
  const lowQuestions = useMemo(
    () =>
      questions.filter((q) => {
        const scale = ratingChoicesForQuestion(q);
        return isLowSurveyRating(ratings[q.surveyQuestionId] || 0, scale.length);
      }),
    [questions, ratings]
  );
  const allRated = questions.every((q) => (ratings[q.surveyQuestionId] || 0) >= 1);
  const lowestRating = useMemo(() => {
    const values = questions.map((q) => ratings[q.surveyQuestionId] || 0).filter((v) => v > 0);
    return values.length ? Math.min(...values) : 0;
  }, [questions, ratings]);
  const selected = useMemo(() => {
    const q =
      questions.find((item) => (ratings[item.surveyQuestionId] || 0) === lowestRating) || questions[0];
    return ratingChoicesForQuestion(q).find((r) => r.value === lowestRating);
  }, [questions, ratings, lowestRating]);

  const [logAction] = useSurveyActionMutation();
  const [submitSurvey] = useSurveySubmitMutation();
  const [laterSurvey] = useSurveyLaterMutation();
  const [closeSurvey] = useSurveyCloseMutation();

  const applyPreviewForceStep = (force: 'rating' | 'feedback' | 'thanks') => {
    setValidationError('');
    setHoverByQuestion({});
    setSubmitting(false);
    if (force === 'rating') {
      setRatings({});
      setReasonByQuestion({});
      setFeedback('');
      setAllowContact(false);
      setEmail(accountEmail);
      setStep('rating');
      return;
    }
    if (force === 'feedback') {
      // Seed a low rating so Action-step reasons / improvement fields render.
      const seeded: Record<number, number> = {};
      questions.forEach((q) => {
        seeded[q.surveyQuestionId] = 2;
      });
      setRatings(seeded);
      setReasonByQuestion({});
      setFeedback('');
      setAllowContact(true);
      setEmail(accountEmail);
      setStep('feedback');
      return;
    }
    setStep('thanks');
  };

  useEffect(() => {
    if (show && survey) {
      openTimeRef.current = new Date();
      setSubmitting(false);
      setValidationError('');
      if (previewMode && previewForceStep) {
        applyPreviewForceStep(previewForceStep);
        return;
      }
      setStep('rating');
      setRatings({});
      setHoverByQuestion({});
      setReasonByQuestion({});
      setFeedback('');
      setAllowContact(false);
      setEmail(accountEmail);
      if (previewMode) return;
      logAction({
        surveyId: survey.surveyId,
        campaignNumber: survey.campaignNumber,
        popupInstanceId: survey.popupInstanceId,
        sessionId: sessionIdRef.current,
        actionType: 'PopupOpened',
        popupOpenTime: openTimeRef.current.toISOString(),
        pageName: window.location.pathname,
        browser: navigator.userAgent,
        device: 'Web',
        operatingSystem: navigator.platform,
      }).catch(() => undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, survey?.popupInstanceId, accountEmail, previewMode, previewForceStep]);

  useEffect(() => {
    if (!show || !accountEmail) return;
    setEmail(accountEmail);
  }, [show, accountEmail]);

  useEffect(() => {
    if (step !== 'thanks') return;
    // Keep thank-you sticky in forced admin preview jumps.
    if (previewMode && previewForceStep === 'thanks') return;
    const timer = setTimeout(() => onClose(), previewMode ? 2500 : 5000);
    return () => clearTimeout(timer);
  }, [step, onClose, previewMode, previewForceStep]);

  if (!survey) return null;

  const staySeconds = () =>
    Math.max(0, Math.round((Date.now() - openTimeRef.current.getTime()) / 1000));

  const basePayload = () => ({
    surveyId: survey.surveyId,
    campaignNumber: survey.campaignNumber,
    popupInstanceId: survey.popupInstanceId,
    sessionId: sessionIdRef.current,
    popupOpenTime: openTimeRef.current.toISOString(),
    stayDurationSeconds: staySeconds(),
    pageName: window.location.pathname,
    browser: navigator.userAgent,
    device: 'Web',
    operatingSystem: navigator.platform,
  });

  const buildAnswersPayload = () =>
    questions.map((q) => {
      const scaleLen = ratingChoicesForQuestion(q).length;
      const rating = ratings[q.surveyQuestionId];
      return {
        surveyQuestionId: q.surveyQuestionId,
        rating,
        reasonId: isLowSurveyRating(rating || 0, scaleLen)
          ? reasonByQuestion[q.surveyQuestionId] || null
          : null,
      };
    });

  const setQuestionRating = async (questionId: number, value: number) => {
    setRatings((prev) => ({ ...prev, [questionId]: value }));
    setValidationError('');
    if (!previewMode) {
      await logAction({
        ...basePayload(),
        actionType: 'RatingSelected',
        rating: value,
        surveyQuestionId: questionId,
      }).catch(() => undefined);
    }
  };

  const submitAllAnswers = async (answers: ReturnType<typeof buildAnswersPayload>) => {
    const headerRating = Math.min(...answers.map((a) => a.rating));
    const firstLow = answers.find((a) => {
      const q = questions.find((item) => item.surveyQuestionId === a.surveyQuestionId);
      return isLowSurveyRating(a.rating || 0, ratingChoicesForQuestion(q).length);
    });
    await submitSurvey({
      ...basePayload(),
      rating: headerRating,
      reasonId: firstLow?.reasonId || undefined,
      answers,
      feedback: feedback.trim() || undefined,
      allowContact,
      email: allowContact ? (email || accountEmail).trim() : '',
    })
      .unwrap()
      .catch(() => undefined);
  };

  const handleContinueFromRatings = async () => {
    if (!allRated) {
      setValidationError('Please rate every question before continuing.');
      return;
    }
    const lows = questions.filter((q) =>
      isLowSurveyRating(ratings[q.surveyQuestionId] || 0, ratingChoicesForQuestion(q).length)
    );
    if (lows.length === 0) {
      if (previewMode) {
        setStep('thanks');
        return;
      }
      setSubmitting(true);
      await submitAllAnswers(buildAnswersPayload());
      setSubmitting(false);
      setStep('thanks');
      return;
    }
    setStep('feedback');
  };

  const handleSubmitFeedback = async () => {
    for (const q of lowQuestions) {
      if (!reasonByQuestion[q.surveyQuestionId]) {
        setValidationError(
          labelKey.surveyReasonRequired ||
            'Please select a feedback option for each low-rated question.'
        );
        return;
      }
    }
    if (!feedback.trim()) {
      setValidationError(
        labelKey.surveyFeedbackRequired ||
          'Please describe what we can improve. Your details help us fix the right things.'
      );
      return;
    }
    const contactEmail = (email || accountEmail).trim();
    if (allowContact && !contactEmail) {
      setValidationError(
        labelKey.surveyEmailRequired || 'Your account email is required so we can follow up.'
      );
      return;
    }

    setValidationError('');
    if (previewMode) {
      setStep('thanks');
      return;
    }
    setSubmitting(true);
    await submitAllAnswers(buildAnswersPayload());
    setSubmitting(false);
    setStep('thanks');
  };

  const handleLater = async () => {
    if (previewMode) {
      onClose();
      return;
    }
    setSubmitting(true);
    await laterSurvey(basePayload()).unwrap().catch(() => undefined);
    setSubmitting(false);
    onClose();
  };

  const handleSkip = async () => {
    if (previewMode) {
      onClose();
      return;
    }
    setSubmitting(true);
    await closeSurvey(basePayload()).unwrap().catch(() => undefined);
    setSubmitting(false);
    onClose();
  };

  const handleClose = async () => {
    if (step === 'thanks') {
      onClose();
      return;
    }
    await handleSkip();
  };

  const secondaryBtnStyle: React.CSSProperties = {
    minWidth: 140,
    borderRadius: 999,
    fontWeight: 700,
    fontSize: '1rem',
    border: '1px solid rgba(255,255,255,0.55)',
    background: 'rgba(255,255,255,0.12)',
    color: '#fff',
    padding: '0.55rem 1.25rem',
  };

  const primaryBtnStyle: React.CSSProperties = {
    minWidth: 140,
    borderRadius: 999,
    fontWeight: 700,
    fontSize: '1rem',
    border: 'none',
    background: '#fff',
    color: '#0b3d5c',
    padding: '0.55rem 1.25rem',
  };

  const lightLabelStyle: React.CSSProperties = {
    color: '#ffffff',
    fontWeight: 700,
    fontSize: '1.05rem',
  };
  const lightHintStyle: React.CSSProperties = {
    color: 'rgba(255,255,255,0.88)',
    fontSize: '0.95rem',
  };
  const lightBodyStyle: React.CSSProperties = {
    color: '#ffffff',
    fontSize: '1.02rem',
  };

  const body = (
    <div className={`survey-modal-body${previewMode ? ' survey-modal-preview' : ''}${embedded ? ' survey-modal-embedded' : ''}`}>
      <style>{`
        .modal-dialog.survey-modal {
          width: 75vw;
          max-width: 75vw;
          max-height: 92vh;
          margin-left: auto;
          margin-right: auto;
        }
        .survey-modal .modal-content {
          background: transparent;
          border: 0;
          max-height: 92vh;
        }
        .survey-modal .modal-body {
          max-height: 92vh;
          overflow-y: auto;
        }
        .survey-modal-body {
          max-height: 92vh;
          overflow-y: auto;
        }
        .survey-modal-embedded {
          width: 100%;
        }
        .survey-modal-embedded .survey-modal-body {
          width: 100%;
        }
        .survey-modal-embedded .form-label,
        .survey-modal-embedded label.form-label,
        .survey-modal-embedded h3,
        .survey-modal-embedded h4,
        .survey-modal-embedded p {
          color: inherit;
        }
        .survey-rating-row {
          display: flex;
          flex-wrap: nowrap;
          justify-content: center;
          align-items: stretch;
          gap: 0.4rem;
          width: 100%;
        }
        .survey-rating-row .survey-rating-btn {
          flex: 1 1 0;
          min-width: 0;
          max-width: 104px;
        }
        .survey-rating-question-block {
          padding-bottom: 0.5rem;
          border-bottom: 1px solid rgba(255,255,255,0.12);
        }
        .survey-rating-question-block:last-child {
          border-bottom: 0;
          padding-bottom: 0;
        }
      `}</style>
      <div
        style={{
          background: 'linear-gradient(165deg, #08324c 0%, #0f5f7e 42%, #1a8fb5 100%)',
          color: '#ffffff',
          borderRadius: '0.65rem',
          padding: '2.25rem 2rem 1.75rem',
          position: 'relative',
          minHeight: 400,
          boxShadow: embedded ? 'none' : '0 18px 40px rgba(8, 40, 60, 0.35)',
        }}
      >
        {previewMode && (
          <div
            style={{
              position: 'absolute',
              top: 12,
              left: 14,
              zIndex: 2,
              background: 'rgba(0,0,0,0.35)',
              border: '1px solid rgba(255,255,255,0.35)',
              color: '#fff',
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 700,
              padding: '4px 10px',
            }}
            title="Admin preview — no survey actions are saved"
          >
            Preview
          </div>
        )}
        <button
          type="button"
          aria-label="Close"
          title="Close"
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: 12,
            right: 14,
            width: 34,
            height: 34,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.14)',
            border: '1px solid rgba(255,255,255,0.25)',
            color: '#fff',
            fontSize: 20,
            lineHeight: 1,
            cursor: 'pointer',
          }}
        >
          ×
        </button>

        {step === 'rating' && (
          <div>
            <div className="text-center mb-5">
              <div
                className="mx-auto mb-3 d-flex align-items-center justify-content-center"
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.14)',
                  fontSize: 30,
                }}
              >
                💬
              </div>
              <h3
                className="fw-bold mb-2"
                style={{ letterSpacing: '-0.02em', color: '#ffffff', fontSize: '1.75rem' }}
              >
                {welcomeHeading}
              </h3>
              <p
                className="mb-0 mx-auto"
                style={{
                  maxWidth: 560,
                  color: 'rgba(255,255,255,0.95)',
                  fontSize: '1.15rem',
                  lineHeight: 1.45,
                }}
              >
                {welcomeMessage}
              </p>
            </div>

            <div className="d-flex flex-column gap-6 mb-5">
              {questions.map((q, index) => {
                const current = ratings[q.surveyQuestionId] || 0;
                const hover = hoverByQuestion[q.surveyQuestionId] || 0;
                return (
                  <div
                    key={`rating-q-${q.surveyQuestionId}-${index}`}
                    className="text-center survey-rating-question-block"
                  >
                    <p
                      className="mb-3 mx-auto fw-semibold"
                      style={{
                        maxWidth: 640,
                        color: 'rgba(255,255,255,0.95)',
                        fontSize: '1.05rem',
                      }}
                    >
                      {questions.length > 1 ? `Q${index + 1}. ` : ''}
                      {q.question}
                    </p>
                    <div className="survey-rating-row mb-1">
                      {ratingChoicesForQuestion(q).map((item) => {
                        const active = (hover || current) === item.value;
                        return (
                          <button
                            key={item.value}
                            type="button"
                            className="btn p-2 survey-rating-btn"
                            disabled={submitting}
                            aria-label={`${item.label} – ${item.description}`}
                            title={`${item.label} – ${item.description}`}
                            onMouseEnter={() =>
                              setHoverByQuestion((prev) => ({
                                ...prev,
                                [q.surveyQuestionId]: item.value,
                              }))
                            }
                            onMouseLeave={() =>
                              setHoverByQuestion((prev) => ({
                                ...prev,
                                [q.surveyQuestionId]: 0,
                              }))
                            }
                            onClick={() => setQuestionRating(q.surveyQuestionId, item.value)}
                            style={{
                              borderRadius: 14,
                              border: active
                                ? '2px solid rgba(255,255,255,0.95)'
                                : '1px solid rgba(255,255,255,0.25)',
                              background: active
                                ? 'rgba(255,255,255,0.2)'
                                : 'rgba(255,255,255,0.08)',
                              color: '#fff',
                              transform: active ? 'translateY(-2px)' : 'none',
                              transition: 'all 0.15s ease',
                            }}
                          >
                            <div style={{ fontSize: 26, lineHeight: 1.1 }}>{item.emoji}</div>
                            <div
                              className="mt-1 lh-sm"
                              style={{
                                color: '#ffffff',
                                fontWeight: 700,
                                fontSize: '0.62rem',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                              }}
                            >
                              {item.label}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {validationError && step === 'rating' && (
              <div className="alert alert-warning py-2 px-3 mb-3 text-dark">{validationError}</div>
            )}

            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <button
                type="button"
                className="btn btn-sm px-4 py-2"
                style={secondaryBtnStyle}
                onClick={handleSkip}
                disabled={submitting}
                title="Ask again next time you sign in"
              >
                {labelKey.surveySkip || 'Skip for now'}
              </button>
              <button
                type="button"
                className="btn btn-sm px-4 py-2"
                style={secondaryBtnStyle}
                onClick={handleLater}
                disabled={submitting}
                title="Snooze this survey for 3 days"
              >
                {labelKey.surveyLater || 'Remind me later'}
              </button>
              <button
                type="button"
                className="btn btn-sm px-4 py-2"
                style={primaryBtnStyle}
                onClick={handleContinueFromRatings}
                disabled={submitting || !allRated}
              >
                Continue
              </button>
            </div>
            <p
              className="mt-3 mb-0 text-center"
              style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' }}
            >
              Skip = next login · Later = remind in 3 days
            </p>
          </div>
        )}

        {step === 'feedback' && (
          <div>
            <div className="d-flex align-items-center gap-3 mb-4">
              <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{
                  width: 96,
                  minHeight: 96,
                  borderRadius: 14,
                  background: 'rgba(255,255,255,0.14)',
                  padding: 8,
                  color: '#ffffff',
                }}
              >
                <span style={{ fontSize: 28 }}>{selected?.emoji || '😐'}</span>
                <span
                  style={{
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    color: '#ffffff',
                    lineHeight: 1.2,
                    textAlign: 'center',
                  }}
                >
                  {selected?.label}
                </span>
              </div>
              <div>
                <h4 className="fw-bold mb-1" style={{ color: '#ffffff', fontSize: '1.45rem' }}>
                  {actionHeading}
                </h4>
                <p className="mb-0" style={lightHintStyle}>
                  {actionMessage}
                </p>
              </div>
            </div>

            {lowQuestions.map((q, index) => {
              const ratedValue = ratings[q.surveyQuestionId] || 0;
              const ratedOpt = ratingChoicesForQuestion(q).find((r) => r.value === ratedValue);
              return (
                <div key={`feedback-q-${q.surveyQuestionId}-${index}`} className="mb-5">
                  <div className="mb-3">
                    <p className="mb-2 fw-semibold" style={lightBodyStyle}>
                      {lowQuestions.length > 1 || questions.length > 1
                        ? `Q${questions.findIndex((x) => x.surveyQuestionId === q.surveyQuestionId) + 1}. `
                        : ''}
                      {q.question}
                    </p>
                    {ratedOpt && (
                      <div
                        className="d-inline-flex align-items-center gap-2 px-3 py-2"
                        style={{
                          borderRadius: 999,
                          background: 'rgba(255,255,255,0.14)',
                          border: '1px solid rgba(255,255,255,0.3)',
                          color: '#ffffff',
                          fontWeight: 600,
                          fontSize: '0.95rem',
                        }}
                        title="Rating already selected on the previous step"
                      >
                        <span style={{ fontSize: 20 }}>{ratedOpt.emoji}</span>
                        <span>{ratedOpt.label}</span>
                      </div>
                    )}
                  </div>
                  <div className="mb-1" style={lightHintStyle}>
                    {labelKey.surveyReasonLabel || 'What needs improvement?'}{' '}
                    <span style={{ color: '#ffe08a' }}>*</span>
                  </div>
                  <div className="mb-2 d-flex flex-wrap gap-2">
                    {(q.reasons || []).map((r) => {
                      const selectedReason = reasonByQuestion[q.surveyQuestionId] === r.surveyReasonId;
                      return (
                        <label
                          key={r.surveyReasonId}
                          className="d-inline-flex align-items-center px-3 py-2 mb-0"
                          style={{
                            cursor: 'pointer',
                            borderRadius: 999,
                            color: '#ffffff',
                            fontWeight: 600,
                            fontSize: '1.02rem',
                            background: selectedReason
                              ? 'rgba(255,255,255,0.28)'
                              : 'rgba(255,255,255,0.12)',
                            border: selectedReason
                              ? '1px solid rgba(255,255,255,0.95)'
                              : '1px solid rgba(255,255,255,0.35)',
                          }}
                        >
                          <input
                            type="radio"
                            className="me-2"
                            name={`surveyReason-${q.surveyQuestionId}`}
                            checked={selectedReason}
                            onChange={() => {
                              setReasonByQuestion((prev) => ({
                                ...prev,
                                [q.surveyQuestionId]: r.surveyReasonId,
                              }));
                              setValidationError('');
                            }}
                          />
                          <span style={lightBodyStyle}>{r.reason}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <label className="form-label" style={lightLabelStyle}>
              {labelKey.surveyImprovementLabel || 'How can we do better?'}{' '}
              <span style={{ color: '#ffe08a' }}>*</span>
            </label>
            <textarea
              className="form-control mb-2"
              rows={3}
              placeholder={
                labelKey.surveyFeedbackPlaceholder ||
                'Example: The expense submit screen is slow, or I could not find the receipt upload option…'
              }
              value={feedback}
              onChange={(e) => {
                setFeedback(e.target.value);
                setValidationError('');
              }}
            />
            <p style={lightHintStyle}>
              {labelKey.surveyImprovementHint ||
                'Share a specific step, screen, or issue so we can prioritize the right fix.'}
            </p>

            <label className="d-flex align-items-center mb-2" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                className="me-2"
                checked={allowContact}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setAllowContact(checked);
                  if (checked) {
                    setEmail(accountEmail || email.trim());
                    setValidationError('');
                  }
                }}
              />
              <span style={lightBodyStyle}>
                {labelKey.surveyAllowContact || 'You may contact me about this feedback'}
              </span>
            </label>
            {allowContact && (
              <div className="mb-2">
                <label className="form-label" style={{ ...lightLabelStyle, fontSize: '1rem' }}>
                  {accountEmail ? 'Contact email' : 'Email'}
                </label>
                <input
                  type="email"
                  className="form-control"
                  readOnly={!!accountEmail}
                  placeholder="Email"
                  value={email || accountEmail}
                  onChange={(e) => {
                    if (accountEmail) return;
                    setEmail(e.target.value);
                    setValidationError('');
                  }}
                  title={
                    accountEmail
                      ? 'Your account email — used if we need to follow up'
                      : 'Enter the email we should use to contact you'
                  }
                />
                <p className="mt-2 mb-6" style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.95rem' }}>
                  {accountEmail
                    ? `Hi ${(authFirstName || '').trim() || 'there'}, we will use your SpendSail account email to contact you.`
                    : 'Enter an email so we can follow up on your feedback.'}
                </p>
              </div>
            )}

            {validationError && (
              <div className="alert alert-warning py-2 px-3 mb-3 text-dark">{validationError}</div>
            )}

            <div className="d-flex justify-content-between gap-2 flex-wrap mt-8">
              <button
                type="button"
                className="btn btn-sm px-3 py-2"
                style={secondaryBtnStyle}
                onClick={() => setStep('rating')}
                disabled={submitting}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn-sm px-4 py-2"
                style={primaryBtnStyle}
                onClick={handleSubmitFeedback}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit feedback'}
              </button>
            </div>
          </div>
        )}

        {step === 'thanks' && (
          <div className="text-center py-8">
            <div style={{ fontSize: 52 }} className="mb-3">
              🎉
            </div>
            <h3 className="fw-bold mb-3" style={{ color: '#ffffff' }}>
              {thanksTitle}
            </h3>
            <p className="mb-0 mx-auto" style={{ maxWidth: 480, ...lightHintStyle }}>
              {thanksMessage}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  if (embedded) {
    if (!show) return null;
    return (
      <div className={`survey-modal survey-modal-embedded${previewMode ? ' survey-modal-preview' : ''}`}>
        {body}
      </div>
    );
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      scrollable
      backdrop="static"
      dialogClassName={`survey-modal${previewMode ? ' survey-modal-preview' : ''}`}
    >
      <Modal.Body className="p-0 border-0 bg-transparent">{body}</Modal.Body>
    </Modal>
  );
};

export default SurveyModal;
